import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  Input,
  Typography,
  Radio,
  useToast,
  Checkbox,
} from '@crisellevaldez/speculo-ui';
import { Plus, X, ExternalLink, Copy } from 'lucide-react';
import { websiteBrandingSchema, WebsiteBrandingData } from '../schemas/onboarding.schema';
import { FormSection } from '../components/FormSection';
import { InfoNotice } from '../components/InfoNotice';
import { QuestionLabel } from '../components/QuestionLabel';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { TipBox } from '../components/TipBox';
import { SectionBox } from '../components/SectionBox';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';
import { ContentBuilder, BuilderContent } from '../builder/ContentBuilder';
import { normalizeBuilderContent } from '../builder/utils/builderContent';
import { TeamContact } from '../components/DesignTeamContact';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useUpdateOrganizationSettings } from '../../../../hooks/organizations/useUpdateOrganizationSettings';
import { useQueryClient } from '@tanstack/react-query';

interface Stage2WebsiteBrandingProps {
  onNext: (data: WebsiteBrandingData) => void;
  initialData?: Partial<WebsiteBrandingData>;
  isLoading?: boolean;
  existingDomain?: string;
}

export const Stage2WebsiteBranding: React.FC<Stage2WebsiteBrandingProps> = ({
  onNext,
  initialData,
  isLoading = false,
  existingDomain = '',
}) => {
  const { organization, fetchOrganization } = useOrganizationStore();
  const { updateSettings } = useUpdateOrganizationSettings();
  const { sendWebhook } = useOnboardingWebhook();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // State for 3-step flow: domain -> builder -> confirmation
  const [currentStep, setCurrentStep] = useState<'domain' | 'builder' | 'confirmation'>(() => {
    // Check if builder was completed (clicked Save & Continue) - go to confirmation
    // API stores keys as snake_case (toSnakeCaseKeys converts builderCompleted → builder_completed)
    if ((organization as any)?.onboardingData?.website?.builder_completed) {
      return 'confirmation';
    }
    // Check if domain setup is complete - if so, go to builder
    const hasDomainSetup =
      initialData?.domain_name && initialData?.domain_ownership && !(initialData as any)?.skipped;

    // If we have builder content AND domain setup, stay in builder (they may have saved draft)
    if (hasDomainSetup && (organization as any)?.onboardingData?.website?.website_builder) {
      return 'builder';
    }

    return hasDomainSetup ? 'builder' : 'domain';
  });

  const [designUpdateEmails, setDesignUpdateEmails] = useState<string[]>(
    (organization as any)?.onboardingData?.website?.design_update_emails ||
      initialData?.design_update_emails ||
      []
  );

  const [newDesignEmail, setNewDesignEmail] = useState('');
  const [additionalFilesLink, setAdditionalFilesLink] = useState<string>(
    (organization as any)?.onboardingData?.website?.additional_files_link || ''
  );
  const [usesGoDaddy, setUsesGoDaddy] = useState<boolean | null>(null);
  const [isSavingDomain, setIsSavingDomain] = useState(false);
  const [isSavingEmails, setIsSavingEmails] = useState(false);
  const [savedBuilderContent, setSavedBuilderContent] = useState<BuilderContent | null>(
    normalizeBuilderContent((organization as any)?.onboardingData?.website?.website_builder)
  );

  const form = useForm<WebsiteBrandingData>({
    resolver: zodResolver(websiteBrandingSchema),
    defaultValues: {
      is_primary_website: initialData?.is_primary_website || undefined,
      domain_name: initialData?.domain_name || '',
      domain_ownership: initialData?.domain_ownership,
      needs_access_help: initialData?.needs_access_help,
      uses_godaddy: initialData?.uses_godaddy,
      domain_registrar: initialData?.domain_registrar || '',
      registrar_username: initialData?.registrar_username || '',
      registrar_password: initialData?.registrar_password || '',
      registrar_email: initialData?.registrar_email || '',
      registrar_phone: initialData?.registrar_phone || '',
    },
  });

  const isPrimaryWebsite = form.watch('is_primary_website');
  const domainOwnership = form.watch('domain_ownership');
  const domainName = form.watch('domain_name');

  // Check if we should skip to Stage 3 (if primary is "no")
  const shouldSkipWebsiteSetup = isPrimaryWebsite === 'no';

  const handleDomainSetupComplete = async (data: WebsiteBrandingData) => {
    if (!organization?.id) return;

    // If skipping website setup, submit minimal data and proceed
    if (shouldSkipWebsiteSetup) {
      sendWebhook('Stage 2 - Website Branding (Skipped)', data);
      onNext(data);
      return;
    }

    setIsSavingDomain(true);
    try {
      // Only save domain-related fields (not the entire form)
      const domainData = {
        is_primary_website: data.is_primary_website,
        domain_name: data.domain_name,
        domain_ownership: data.domain_ownership,
        needs_access_help: data.needs_access_help,
        uses_godaddy: data.uses_godaddy,
        domain_registrar: data.domain_registrar,
        registrar_username: data.registrar_username,
        registrar_password: data.registrar_password,
        registrar_email: data.registrar_email,
        registrar_phone: data.registrar_phone,
      };

      // Remove undefined values from data
      const cleanData = Object.entries(domainData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      // Save domain setup data - merge with existing website data to preserve builder content
      const existingWebsiteData = (organization as any)?.onboardingData?.website || {};
      await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...(useOrganizationStore.getState().organization as any)?.onboardingData ?? {},
            website: {
              ...existingWebsiteData,
              ...cleanData,
            },
          },
        } as any,
      });

      // Refetch organization data to ensure cache is updated
      await queryClient.refetchQueries({ queryKey: ['Organizations'] });
      await fetchOrganization(true);

      // Don't send webhook yet - only send when stage is fully complete
      // Show builder after domain save
      setCurrentStep('builder');
    } catch (error) {
      console.error('Error saving domain setup:', error);
      showToast({
        message: 'Failed to save domain setup. Please try again.',
      });
    } finally {
      setIsSavingDomain(false);
    }
  };

  const handleBuilderSave = async (builderContent: BuilderContent, isDraft?: boolean) => {
    if (!organization?.id) return;

    try {
      // Save builder content merged into existing website data
      // Also mark builderCompleted if this is NOT a draft
      await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...(useOrganizationStore.getState().organization as any)?.onboardingData ?? {},
            website: {
              ...((organization as any)?.onboardingData?.website ?? {}),
              website_builder: builderContent,
              ...(!isDraft && { builder_completed: true }),
            },
          },
        } as any,
      });

      // Refetch organization data to ensure cache is updated
      await queryClient.refetchQueries({ queryKey: ['Organizations'] });
      await fetchOrganization(true);

      setSavedBuilderContent(builderContent);

      // Show success message
      showToast({
        message: isDraft ? 'Draft saved successfully' : 'Website design saved successfully',
        variant: 'success',
      });

      // Only proceed to confirmation step if NOT a draft
      if (!isDraft) {
        // Go to confirmation step to collect design team emails
        setCurrentStep('confirmation');
      }
    } catch (error) {
      console.error('Error saving builder content:', error);
      showToast({
        message: 'Failed to save. Please try again.',
      });
    }
  };

  // Handler for confirmation step
  const handleConfirmationComplete = async () => {
    if (!organization?.id) return;

    setIsSavingEmails(true);
    try {
      // Save design team emails and additional files link
      await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...(useOrganizationStore.getState().organization as any)?.onboardingData ?? {},
            website: {
              ...((organization as any)?.onboardingData?.website ?? {}),
              design_update_emails: designUpdateEmails,
              additional_files_link: additionalFilesLink || '',
            },
          },
        } as any,
      });

      // Fall back to organization data if savedBuilderContent is not available
      const builderContent =
        savedBuilderContent ||
        normalizeBuilderContent((organization as any)?.onboardingData?.website?.website_builder);

      // Combine form data with design update emails, additional files link, and builder content
      const completeData = {
        ...form.getValues(),
        design_update_emails: designUpdateEmails,
        additional_files_link: additionalFilesLink,
        websiteBuilder: builderContent,
      };

      // Send webhook notification
      sendWebhook('Stage 2 - Website Branding Complete', completeData);

      // Proceed to next stage
      onNext(completeData);
    } catch (error) {
      console.error('Error saving design emails:', error);
      showToast({
        message: 'Failed to save. Please try again.',
      });
    } finally {
      setIsSavingEmails(false);
    }
  };

  // STEP 3: Show confirmation with TeamContact
  if (currentStep === 'confirmation' && organization?.id) {
    return (
      <StageContainer
        title="Website Setup Complete!"
        description="Your website content has been saved. Let's set up notifications for design updates."
      >
        <div className="space-y-6">
          <TeamContact
            title="✅ Website Content Complete!"
            description="Our design team will transform your content into a stunning final website using your brand colors, custom fonts, and professional layouts."
            teamLeadName="Christian"
            teamLeadRole="Website Developer/ Lead"
            teamLeadEmail="christian.thespeculogroup@gmail.com"
            teamLeadImage="/onboarding/CHRISTIAN.webp"
            helperText=""
          />

          <FormSection>
            <Form.Control required>
              <QuestionLabel>
                Who should receive website design updates and change requests?
              </QuestionLabel>

              <div className="flex gap-2">
                <Input
                  value={newDesignEmail}
                  onChange={(e) => setNewDesignEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (
                        newDesignEmail &&
                        newDesignEmail.includes('@') &&
                        !designUpdateEmails.includes(newDesignEmail)
                      ) {
                        setDesignUpdateEmails([...designUpdateEmails, newDesignEmail]);
                        setNewDesignEmail('');
                      }
                    }
                  }}
                  placeholder="email@example.com"
                  type="email"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (
                      newDesignEmail &&
                      newDesignEmail.includes('@') &&
                      !designUpdateEmails.includes(newDesignEmail)
                    ) {
                      setDesignUpdateEmails([...designUpdateEmails, newDesignEmail]);
                      setNewDesignEmail('');
                    }
                  }}
                  disabled={!newDesignEmail || !newDesignEmail.includes('@')}
                  className="inline-flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </Button>
              </div>

              {designUpdateEmails.length > 0 && (
                <div className="space-y-2 mt-3">
                  {designUpdateEmails.map((email, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-2 rounded border border-gray-200"
                    >
                      <span className="text-sm text-gray-700">{email}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setDesignUpdateEmails(designUpdateEmails.filter((_, i) => i !== index))
                        }
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <TipBox>
                These contacts will receive notifications about website updates, design changes, and
                implementation progress from the design team.
              </TipBox>

              {designUpdateEmails.length === 0 && (
                <p className="text-sm text-red-600 mt-2">Please add at least one email address</p>
              )}
            </Form.Control>
          </FormSection>

          {/* Optional: Share additional files */}
          <FormSection>
            <div className="space-y-2">
              <QuestionLabel required={false}>
                Share more files with the design team{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </QuestionLabel>
              <Input
                value={additionalFilesLink}
                onChange={(e) => setAdditionalFilesLink(e.target.value)}
                placeholder="https://drive.google.com/... or any shareable link"
              />
              <TipBox>
                If you have additional assets, style guides, brand guidelines, or other files to
                share with our design team, paste a Google Drive, Dropbox, or other shareable link
                here.
              </TipBox>
            </div>
          </FormSection>

          <div className="flex justify-between items-center gap-3">
            <NextButton type="button" onClick={() => setCurrentStep('builder')} variant="secondary">
              Back to Builder
            </NextButton>
            <NextButton
              type="button"
              onClick={handleConfirmationComplete}
              isLoading={isSavingEmails}
              disabled={designUpdateEmails.length === 0}
            >
              Complete Website Setup
            </NextButton>
          </div>
        </div>
      </StageContainer>
    );
  }

  // STEP 2: Show visual builder
  if (currentStep === 'builder' && organization?.id) {
    return (
      <ContentBuilder
        organizationId={organization.id}
        onSave={handleBuilderSave}
        onBack={() => setCurrentStep('domain')}
        initialContent={
          normalizeBuilderContent((organization as any)?.onboardingData?.website?.website_builder) || {
            hero: {
              backgroundImage: '',
              logo: '',
              headline: '[Headline]',
              tagline: '[Tag Line]',
              primaryCTA: 'BUY',
              secondaryCTA: 'SELL',
              stats: [
                { value: '[$2M+]', label: '[CAREER SALES]' },
                { value: '[500+]', label: '[HOMES SOLD]' },
                { value: '[Top 10%]', label: '[RANK / YEARS OF EXPERIENCE]' },
                { value: '[10+]', label: '[AWARDS]' },
              ],
            },
            about: {
              teamPhoto: '',
              companyName: '[Team Name]',
              bio: '[Share your story, experience, and what makes your team unique.]',
            },
          }
        }
      />
    );
  }

  return (
    <StageContainer
      title="Website Branding"
      description="Let's set up your website. This will help us create a professional online presence for your business."
    >
      <form
        onSubmit={form.handleSubmit(handleDomainSetupComplete)}
        className="space-y-8"
        noValidate
      >
        {/* INTRO SECTION */}
        <InfoNotice>
          We'll help you create a new custom branded website to replace your primary/main website
          and support your AI, Remi.
        </InfoNotice>

        {/* WEBSITE TYPE SELECTION */}
        <div className="space-y-6">
          <Typography variant="h2" className="text-xl font-bold text-gray-900 border-b pb-2">
            Website Type
          </Typography>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>Is Speculo replacing your primary/main website?</QuestionLabel>
              <Controller
                control={form.control}
                name="is_primary_website"
                render={({ field }) => (
                  <div className="space-y-2">
                    <div className="space-y-2">
                      <Radio
                        id="primary-yes"
                        value="yes"
                        checked={field.value === 'yes'}
                        onChange={() => field.onChange('yes')}
                        label="Yes"
                      />
                      <Radio
                        id="primary-no"
                        value="no"
                        checked={field.value === 'no'}
                        onChange={() => field.onChange('no')}
                        label="No"
                      />
                    </div>
                    {form.formState.errors.is_primary_website && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.is_primary_website.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </Form.Control>
          </FormSection>

          {shouldSkipWebsiteSetup && (
            <InfoNotice>
              💡 You've indicated that Speculo won't be creating a website for you. Click Next to
              continue to the AI Assistant setup.
            </InfoNotice>
          )}
        </div>

        {/* DOMAIN SETUP - Only show if creating a website */}
        {isPrimaryWebsite === 'yes' && (
          <div className="space-y-6">
            <Typography variant="h2" className="text-xl font-bold text-gray-900 border-b pb-2">
              Domain Setup
            </Typography>

            <InfoNotice>
              Now let's set up the domain for your new website. You can use your existing domain or
              choose a new one.
            </InfoNotice>

            <FormSection>
              <Form.Control>
                <QuestionLabel>
                  {isPrimaryWebsite === 'yes'
                    ? existingDomain
                      ? `Will you continue using ${existingDomain} for your new Speculo website?`
                      : 'What domain would you like to use for your custom website?'
                    : 'Please enter a new domain to be used for your website'}
                </QuestionLabel>
                <Input
                  {...form.register('domain_name')}
                  placeholder="e.g., yourcompany.com"
                  error={form.formState.errors.domain_name?.message}
                />

                <Controller
                  control={form.control}
                  name="domain_ownership"
                  render={({ field }) => (
                    <div className="space-y-2 mt-4">
                      <div className="space-y-2">
                        <Radio
                          id="domain-own"
                          value="own"
                          checked={field.value === 'own'}
                          onChange={() => field.onChange('own')}
                          label="I already own this domain"
                        />
                        <Radio
                          id="domain-purchase"
                          value="purchase"
                          checked={field.value === 'purchase'}
                          onChange={() => field.onChange('purchase')}
                          label="Please purchase this domain on my behalf"
                        />
                      </div>
                      {form.formState.errors.domain_ownership && (
                        <p className="text-sm text-red-600">
                          {form.formState.errors.domain_ownership.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </Form.Control>
            </FormSection>

            {domainOwnership === 'own' && (
              <SectionBox variant="gray" type="child">
                <FormSection>
                  <Form.Control required>
                    <QuestionLabel>Share your domain access</QuestionLabel>
                    <Controller
                      control={form.control}
                      name="needs_access_help"
                      render={({ field }) => (
                        <div className="space-y-2">
                          <div className="space-y-2">
                            <Radio
                              id="godaddy-yes"
                              value="yes"
                              checked={usesGoDaddy === true}
                              onChange={() => {
                                setUsesGoDaddy(true);
                                field.onChange(true);
                                form.setValue('uses_godaddy', true);
                              }}
                              label="I use GoDaddy"
                            />
                            <Radio
                              id="godaddy-no"
                              value="no"
                              checked={usesGoDaddy === false}
                              onChange={() => {
                                setUsesGoDaddy(false);
                                field.onChange(true);
                                form.setValue('uses_godaddy', false);
                              }}
                              label="I use other domain provider"
                            />
                          </div>
                          {form.formState.errors.needs_access_help && (
                            <p className="text-sm text-red-600">
                              {form.formState.errors.needs_access_help.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </Form.Control>
                </FormSection>

                {usesGoDaddy === true && (
                  <InfoNotice>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <p>💬 Great! You can delegate access without sharing passwords.</p>
                        <a
                          href="https://www.loom.com/share/69b30ee489324de394a3c2894b0ada7a"
                          className="text-blue-600 hover:underline font-semibold inline-flex items-center gap-1"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Watch how to delegate GoDaddy access
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>

                      {/* Share Info - Under the instructions */}
                      <div className="pt-3 border-t border-blue-200">
                        <Typography variant="h3" className="text-sm font-semibold mb-2">
                          📋 Share access with:
                        </Typography>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {/* Name Field */}
                          <div className="grid grid-cols-[50px_1fr_auto] items-center gap-2">
                            <span className="font-semibold text-xs">Name:</span>
                            <code className="bg-white px-2 py-1 rounded text-xs truncate">
                              Speculo
                            </code>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText('Speculo');
                                showToast({
                                  message: 'Name copied to clipboard',
                                  variant: 'success',
                                });
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>

                          {/* Email Field */}
                          <div className="grid grid-cols-[50px_1fr_auto] items-center gap-2">
                            <span className="font-semibold text-xs">Email:</span>
                            <code className="bg-white px-2 py-1 rounded text-xs truncate">
                              christian.thespeculogroup@gmail.com
                            </code>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  'christian.thespeculogroup@gmail.com'
                                );
                                showToast({
                                  message: 'Email copied to clipboard',
                                  variant: 'success',
                                });
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Access Shared Checkbox - Required */}
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <Controller
                            control={form.control}
                            name="godaddy_access_shared"
                            render={({ field }) => (
                              <div>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    id="godaddy-access-shared"
                                    checked={field.value || false}
                                    disabled={!domainName?.trim()}
                                    onChange={(e) => {
                                      if (!domainName?.trim()) {
                                        showToast({
                                          message: 'Please enter your domain name first',
                                        });
                                        return;
                                      }
                                      field.onChange(e.target.checked);
                                      if (e.target.checked) {
                                        sendWebhook('Stage 2 - GoDaddy Access Shared', {
                                          domain_name: form.getValues('domain_name'),
                                          godaddy_access_shared: true,
                                          timestamp: new Date().toISOString(),
                                        });
                                        showToast({
                                          message: 'Thank you! We will verify the access soon.',
                                          variant: 'success',
                                        });
                                      }
                                    }}
                                    label="I have shared access with Speculo"
                                  />
                                  <span className="text-red-500 text-sm">*</span>
                                </div>
                                {!domainName?.trim() && (
                                  <p className="text-sm text-amber-600 mt-1">
                                    Please fill in the domain name above before confirming
                                  </p>
                                )}
                                {form.formState.errors.godaddy_access_shared && (
                                  <p className="text-sm text-red-600 mt-1">
                                    {form.formState.errors.godaddy_access_shared.message}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </InfoNotice>
                )}

                {usesGoDaddy === false && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormSection>
                      <Form.Control required>
                        <QuestionLabel>
                          Where did you purchase your domain? (e.g., GoDaddy, Namecheap, etc.)
                        </QuestionLabel>
                        <Input
                          {...form.register('domain_registrar')}
                          placeholder="e.g., GoDaddy, Namecheap, etc."
                          error={form.formState.errors.domain_registrar?.message}
                        />
                      </Form.Control>
                    </FormSection>

                    <FormSection>
                      <Form.Control required>
                        <QuestionLabel>Registrar Username</QuestionLabel>
                        <Input
                          {...form.register('registrar_username')}
                          placeholder="Username"
                          error={form.formState.errors.registrar_username?.message}
                        />
                      </Form.Control>
                    </FormSection>

                    <FormSection>
                      <Form.Control required>
                        <QuestionLabel>Registrar Password</QuestionLabel>
                        <Input
                          {...form.register('registrar_password')}
                          type="password"
                          placeholder="Password"
                          error={form.formState.errors.registrar_password?.message}
                        />
                      </Form.Control>
                    </FormSection>

                    <FormSection>
                      <Form.Control required>
                        <QuestionLabel>Account Owner Email</QuestionLabel>
                        <Input
                          {...form.register('registrar_email')}
                          type="email"
                          placeholder="email@example.com"
                          error={form.formState.errors.registrar_email?.message}
                        />
                      </Form.Control>
                    </FormSection>

                    <FormSection>
                      <Form.Control required>
                        <QuestionLabel>Account Owner Phone (for 2FA if needed)</QuestionLabel>
                        <Input
                          {...form.register('registrar_phone')}
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          error={form.formState.errors.registrar_phone?.message}
                        />
                      </Form.Control>
                    </FormSection>
                  </div>
                )}
              </SectionBox>
            )}
          </div>
        )}

        {/* CONTINUE BUTTON - After domain setup */}
        {isPrimaryWebsite === 'yes' && domainOwnership && (
          <div className="space-y-6">
            <InfoNotice>
              <div className="space-y-3">
                <Typography variant="h3" className="text-lg font-semibold text-gray-900">
                  ✅ Domain Setup Complete!
                </Typography>
                <p className="text-gray-700">
                  Now let's build your website with our visual page builder. You'll be able to
                  customize your hero section with drag-and-drop images and inline text editing.
                </p>
              </div>
            </InfoNotice>
            <NextButton isLoading={isSavingDomain} />
          </div>
        )}

        {/* Submit Button - Only show if skipping or before domain setup */}
        {(shouldSkipWebsiteSetup || !domainOwnership) && <NextButton isLoading={isLoading} />}
      </form>
    </StageContainer>
  );
};
